import { useCallback } from 'react';

import { ApplicationTypes } from '@/models/ApplicationType';
import { PartnerProps } from '@/models/Partner';
import { PartnerYearsRecognized } from '@/models/PartnerYearsRecognized';
import { GrowthAwardProgramIds, ProgramNames } from '@/models/Program';

import { ModalTab } from '../models/modalTabs';

type Args = {
  partnerInfoLocal: PartnerProps;
  selectedProgramId: ProgramNames;
  postPartnerYearsRecognized: (
    partnerId: number,
    programId: ProgramNames,
    applicationTypeId: ApplicationTypes,
    partnerYearsRecognized: PartnerYearsRecognized[]
  ) => Promise<PartnerYearsRecognized[]>;
  setAlert: (a: { message: string; severity: 'warning' | 'success' | 'error' }) => void;
  setSavingTab: (tab: ModalTab | null) => void;
  setSavingAwards: (saving: boolean) => void;
  commitUpdatedPartner: (partner: PartnerProps) => void;
  markTabClean: (tab: ModalTab) => void;
  markTabSaved: (tab: ModalTab) => void;
  markTabError: (tab: ModalTab) => void;
};

export function useAwardsTabSave({
  partnerInfoLocal,
  selectedProgramId,
  postPartnerYearsRecognized,
  setAlert,
  setSavingTab,
  setSavingAwards,
  commitUpdatedPartner,
  markTabClean,
  markTabSaved,
  markTabError,
}: Args) {
  return useCallback(async () => {
    if (partnerInfoLocal.id == null) return;

    setSavingTab('awards');
    setSavingAwards(true);

    try {
      const draftYears = partnerInfoLocal.partnerYearsRecognized ?? [];

      const standardDraftYears = draftYears
        .filter(
          (pyr) =>
            pyr.programId === selectedProgramId &&
            pyr.applicationTypeId === ApplicationTypes.Standard
        )
        .map((pyr) => ({ ...pyr, partnerId: partnerInfoLocal.id! }));

      const standardUpdated = await postPartnerYearsRecognized(
        partnerInfoLocal.id,
        selectedProgramId,
        ApplicationTypes.Standard,
        standardDraftYears
      );

      const updatedForProgram: PartnerYearsRecognized[] = [...standardUpdated];

      if (GrowthAwardProgramIds.includes(selectedProgramId)) {
        const growthDraftYears = draftYears
          .filter(
            (pyr) =>
              pyr.programId === selectedProgramId &&
              pyr.applicationTypeId === ApplicationTypes.GrowthAward
          )
          .map((pyr) => ({ ...pyr, partnerId: partnerInfoLocal.id! }));

        const growthUpdated = await postPartnerYearsRecognized(
          partnerInfoLocal.id,
          selectedProgramId,
          ApplicationTypes.GrowthAward,
          growthDraftYears
        );

        updatedForProgram.push(...growthUpdated);
      }

      const nonSelectedProgramYears = (partnerInfoLocal.partnerYearsRecognized ?? []).filter(
        (pyr) => pyr.programId !== selectedProgramId
      );

      commitUpdatedPartner({
        ...partnerInfoLocal,
        partnerYearsRecognized: [...nonSelectedProgramYears, ...updatedForProgram],
      });

      setAlert({
        message: 'Awards were updated',
        severity: 'success',
      });
      markTabClean('awards');
      markTabSaved('awards');
    } catch (error) {
      markTabError('awards');
      setAlert({
        severity: 'error',
        message: `Error updating partner years recognized. Please try again. ${error instanceof Error ? error.message : ''}`,
      });
    } finally {
      setSavingAwards(false);
      setSavingTab(null);
    }
  }, [
    commitUpdatedPartner,
    markTabClean,
    markTabError,
    markTabSaved,
    partnerInfoLocal,
    postPartnerYearsRecognized,
    selectedProgramId,
    setAlert,
    setSavingAwards,
    setSavingTab,
  ]);
}
